import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from '../firebase';
import { ADMIN_CONFIG } from '../config/adminConfig';

export type AdminInviteStatus = 'pending' | 'accepted' | 'expired' | 'revoked';

export interface AdminInvite {
  id: string;
  email: string;
  invitedBy: string;
  status: AdminInviteStatus;
  inviteToken: string;
  invitedAt: Timestamp;
  expiresAt: Timestamp;
  acceptedAt?: Timestamp | null;
}

export interface AdminUser {
  id: string;
  email: string;
  addedBy: string;
  addedAt: Timestamp;
  isActive: boolean;
}

const ADMIN_INVITES_COLLECTION = 'adminInvites';
const ADMIN_USERS_COLLECTION = 'adminUsers';

const generateInviteToken = () =>
  Math.random().toString(36).slice(2, 10) + Math.random().toString(36).slice(2, 10);

const normalizeEmail = (email: string) => email.trim().toLowerCase();

class AdminInviteService {
  /**
   * Create a new admin invite
   */
  async createInvite(email: string, invitedBy: string): Promise<AdminInvite> {
    const normalizedEmail = normalizeEmail(email);

    const inviteDoc = await addDoc(collection(db, ADMIN_INVITES_COLLECTION), {
      email: normalizedEmail,
      invitedBy,
      status: 'pending',
      inviteToken: generateInviteToken(),
      invitedAt: serverTimestamp(),
      expiresAt: Timestamp.fromDate(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)),
      acceptedAt: null,
    });

    const storedInvite = await getDoc(inviteDoc);
    return { id: inviteDoc.id, ...(storedInvite.data() as Omit<AdminInvite, 'id'>) };
  }

  /**
   * Return all admin invites (for settings UI)
   */
  async listInvites(): Promise<AdminInvite[]> {
    const snapshot = await getDocs(collection(db, ADMIN_INVITES_COLLECTION));
    return snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...(docSnap.data() as Omit<AdminInvite, 'id'>),
    }));
  }

  /**
   * Update invite status (accepted, expired, revoked)
   */
  async updateInviteStatus(inviteId: string, status: AdminInviteStatus): Promise<void> {
    const inviteRef = doc(db, ADMIN_INVITES_COLLECTION, inviteId);
    await updateDoc(inviteRef, {
      status,
      ...(status === 'accepted' ? { acceptedAt: serverTimestamp() } : {}),
    });
  }

  async revokeInvite(inviteId: string): Promise<void> {
    await this.updateInviteStatus(inviteId, 'revoked');
  }

  /**
   * Validate an invite token for signup
   */
  async validateInviteToken(token: string, email: string): Promise<AdminInvite | null> {
    const normalizedEmail = normalizeEmail(email);
    const q = query(
      collection(db, ADMIN_INVITES_COLLECTION),
      where('email', '==', normalizedEmail),
      where('inviteToken', '==', token),
      where('status', '==', 'pending')
    );

    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }

    const inviteDoc = snapshot.docs[0];
    const invite = inviteDoc.data() as AdminInvite;
    const expired = invite.expiresAt.toDate().getTime() < Date.now();

    if (expired) {
      await this.updateInviteStatus(inviteDoc.id, 'expired');
      return null;
    }

    return { ...invite, id: inviteDoc.id };
  }

  /**
   * Mark invite as accepted and persist admin user entry
   */
  async acceptInvite(invite: AdminInvite, addedBy?: string): Promise<AdminUser> {
    await this.updateInviteStatus(invite.id, 'accepted');
    return this.addAdminUser(invite.email, addedBy || invite.invitedBy);
  }

  /**
   * Get list of admin users (dynamic list)
   */
  async listAdminUsers(): Promise<AdminUser[]> {
    const snapshot = await getDocs(collection(db, ADMIN_USERS_COLLECTION));
    return snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...(docSnap.data() as Omit<AdminUser, 'id'>),
    }));
  }

  /**
   * Add admin user (and mark as active)
   */
  async addAdminUser(email: string, addedBy: string): Promise<AdminUser> {
    const normalizedEmail = normalizeEmail(email);
    const adminDoc = await addDoc(collection(db, ADMIN_USERS_COLLECTION), {
      email: normalizedEmail,
      addedBy,
      addedAt: serverTimestamp(),
      isActive: true,
    });

    // Also append to ADMIN_CONFIG runtime list to allow access until config persisted
    if (!ADMIN_CONFIG.ADMIN_EMAILS.includes(normalizedEmail)) {
      ADMIN_CONFIG.ADMIN_EMAILS.push(normalizedEmail);
    }

    const stored = await getDoc(adminDoc);
    return { id: adminDoc.id, ...(stored.data() as Omit<AdminUser, 'id'>) };
  }

  async toggleAdminStatus(adminId: string, isActive: boolean): Promise<void> {
    const adminRef = doc(db, ADMIN_USERS_COLLECTION, adminId);
    await updateDoc(adminRef, { isActive });
  }

  async removeAdminUser(adminId: string): Promise<void> {
    const adminRef = doc(db, ADMIN_USERS_COLLECTION, adminId);
    const adminDoc = await getDoc(adminRef);
    if (adminDoc.exists()) {
      const data = adminDoc.data() as AdminUser;
      // Remove from runtime list
      const idx = ADMIN_CONFIG.ADMIN_EMAILS.indexOf(data.email);
      if (idx >= 0) {
        ADMIN_CONFIG.ADMIN_EMAILS.splice(idx, 1);
      }
    }
    await deleteDoc(adminRef);
  }

  async isEmailAdmin(email: string): Promise<boolean> {
    const normalizedEmail = normalizeEmail(email);

    if (ADMIN_CONFIG.ADMIN_EMAILS.includes(normalizedEmail)) {
      return true;
    }

    try {
      const q = query(
        collection(db, ADMIN_USERS_COLLECTION),
        where('email', '==', normalizedEmail),
        where('isActive', '==', true)
      );

      const snapshot = await getDocs(q);
      return !snapshot.empty;
    } catch {
      // Non-admins cannot list/query adminUsers; treat as not admin without noisy errors.
      return false;
    }
  }

  /**
   * Writes `adminUsers/{firebaseAuthUid}` when the user was invited via a legacy random-id row.
   * Firestore `isAdmin()` checks `adminUsers/$(request.auth.uid)` with matching email.
   */
  async ensureAdminUserUidSync(user: { uid: string; email: string | null }): Promise<void> {
    const email = normalizeEmail(user.email || '');
    if (!email) return;

    const uidRef = doc(db, ADMIN_USERS_COLLECTION, user.uid);
    const uidSnap = await getDoc(uidRef);
    if (uidSnap.exists()) return;

    const q = query(
      collection(db, ADMIN_USERS_COLLECTION),
      where('email', '==', email),
      where('isActive', '==', true)
    );
    const snap = await getDocs(q);
    if (snap.empty) return;

    const legacy = snap.docs.find((d) => d.id !== user.uid) ?? snap.docs[0];
    if (legacy.id === user.uid) return;

    const legacyData = legacy.data() as Omit<AdminUser, 'id'>;

    await setDoc(
      uidRef,
      {
        email,
        isActive: true,
        addedBy: legacyData.addedBy || 'invite-sync',
        addedAt: legacyData.addedAt ?? serverTimestamp(),
        migratedFromDocId: legacy.id,
      },
      { merge: true }
    );
  }
}

export const adminInviteService = new AdminInviteService();









